<template>
  <v-dialog
    v-model="show"
    max-width="500px"
    scrollable
    persistent
  >
    <v-card
      :loading="formProcessing"
    >
      <v-card-title class="font-weight-bold">
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-form>
            <v-row>
              <v-col
                cols="12"
                md="12"
              >
                <v-text-field
                  v-model="companyName"
                  required
                  label="Company Name"
                  :error-messages="validation(v$.companyName, 'Company Name')"
                  @input="v$.companyName.$touch()"
                  @blur="v$.companyName.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="category"
                  :items="categories"
                  label="Category"
                  :error-messages="validation(v$.category, 'Category')"
                  @update:model-value="v$.category.$touch()"
                  @blur="v$.category.$touch()"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="providerType"
                  :items="providerTypes"
                  label="Provider Type"
                  :error-messages="validation(v$.providerType, 'Provider Type')"
                  @update:model-value="v$.providerType.$touch()"
                  @blur="v$.providerType.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="firstName"
                  required
                  label="First Name"
                  :error-messages="validation(v$.firstName, 'First Name')"
                  @input="v$.firstName.$touch()"
                  @blur="v$.firstName.$touch()"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="lastName"
                  required
                  label="Last Name"
                  :error-messages="validation(v$.lastName, 'Last Name')"
                  @input="v$.lastName.$touch()"
                  @blur="v$.lastName.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="username"
                  required
                  label="Username"
                  :error-messages="validation(v$.username, 'Username')"
                  @input="v$.username.$touch()"
                  @blur="v$.username.$touch()"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="email"
                  required
                  label="Email"
                  :error-messages="validation(v$.email, 'Email')"
                  @input="v$.email.$touch()"
                  @blur="v$.email.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="password"
                  required
                  label="Password"
                  :error-messages="validation(v$.password, 'Password')"
                  @input="v$.password.$touch()"
                  @blur="v$.password.$touch()"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="passwordConfirm"
                  required
                  label="Confirm Password"
                  :error-messages="validation(v$.passwordConfirm, 'Confirm Password')"
                  @input="v$.passwordConfirm.$touch()"
                  @blur="v$.passwordConfirm.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="12"
              >
                <v-text-field
                  v-model="apiKey"
                  label="Api Key"
                  required
                  disabled
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="blue darken-1"
          text
          :disabled="formProcessing"
          @click="close()"
        >
          Cancel
        </v-btn>

        <v-btn
          color="blue darken-1"
          text
          :disabled="formProcessing"
          @click="save()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import {
  required, email, minLength, sameAs,
} from '@vuelidate/validators';
import { mapActions } from 'vuex';
import { v4 as uuidv4 } from 'uuid';

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editedItem: {
      type: Object,
      default() {
        return {
          id: null,
          category: '',
          provider_type: '',
          company_name: '',
          first_name: '',
          last_name: '',
          username: '',
          email: '',
          password: '',
          api_key: '',
        };
      },
    },
  },
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      categories: ['Walkup', 'Return', 'Purchased', 'Prescreen', 'Shortform'],
      providerTypes: ['Direct', 'Lead'],
      id: null,
      companyName: '',
      category: '',
      providerType: '',
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
      passwordConfirm: '',
      apiKey: this.editedItem.api_key || uuidv4(),
      status: null,
      formProcessing: false,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    formTitle() {
      return this.isNewItem ? 'Add New Lead Provider' : 'Edit Lead Provider';
    },
    isNewItem() {
      return !this.editedItem.id;
    },
  },
  watch: {
    editedItem() {
      this.id = this.editedItem.id;
      this.category = this.editedItem.category;
      this.providerType = this.editedItem.provider_type;
      this.companyName = this.editedItem.company_name;
      this.firstName = this.editedItem.first_name;
      this.lastName = this.editedItem.last_name;
      this.username = this.editedItem.username;
      this.email = this.editedItem.email;
      this.password = this.editedItem.password;
      this.passwordConfirm = this.editedItem.password;
      this.apiKey = this.editedItem.api_key || uuidv4();
      this.status = this.editedItem.status;
    },
  },
  methods: {
    ...mapActions('notification', ['setNotification']),

    save() {
      this.v$.$touch();

      if (!this.v$.$invalid) {
        this.formProcessing = true;

        let request = '';

        let data = {
          status: this.status,
          category: this.category,
          providerType: this.providerType,
          companyName: this.companyName,
          firstName: this.firstName,
          lastName: this.lastName,
          username: this.username,
          email: this.email,
          password: this.password,
          password_confirmation: this.passwordConfirm,
        };

        if (this.isNewItem) {
          data = {
            ...data,
            status: 1,
            apiKey: this.apiKey,
          };

          request = this.$axios.post('/api/v1/lead-provider', data);
        } else {
          request = this.$axios.put(`/api/v1/lead-provider/${this.editedItem.id}`, data);
        }

        request
          .then((res) => {
            this.$emit('item-saved');

            this.setNotification({
              message: `${res.data.company_name} has been saved.`,
              color: 'green',
            });
          })
          .catch(() => {
            this.setNotification({
              message: 'There was an error processing your request.',
              color: 'red',
            });
          })
          .then(() => {
            this.formProcessing = false;

            this.close();
          });
      }
    },
    close() {
      this.v$.$reset();

      this.$emit('close-modal');
    },

    validation(field, name) {
      const errors = [];

      if (!field.$dirty) {
        return errors;
      }

      // Check for specific validators with custom messages - RETURN EARLY
      if (field.minLength && field.$anyError) {
        errors.push(`${name} must have at least ${field.$params.minLength.min} characters`);
        return errors; // Early return prevents fallthrough
      }

      if (field.email && field.$anyError) {
        errors.push('Must be a valid email');
        return errors; // Early return
      }

      if (field.sameAs) {
        // Check if sameAs validator failed
        if (field.$anyError) {
          errors.push('Passwords must match');
        }
        return errors; // Return early for sameAs fields (no fallthrough)
      }

      // Only reach here if no specific validators matched above
      // Show "is Required" for required validator failures
      if (field.$error) {
        errors.push(`${name} is Required`);
      }

      // Catch-all for any remaining errors
      if (field.$anyError) {
        errors.push(`${name} is Not Valid`);
      }

      return errors;
    },
  },
  validations() {
    return {
      companyName: { required },
      category: { required },
      providerType: { required },
      firstName: { required },
      lastName: { required },
      username: { required },
      email: { email, required },
      password: { minLength: minLength(5), required },
      passwordConfirm: {
        sameAs: sameAs(this.password),
      },
    };
  },
};
</script>

<style></style>
